import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Grid,
    TextField,
    Button,
    Divider,
    Chip,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    AccountBalanceWallet,
    Storefront,
    Receipt,
    ShoppingCart,
    ExpandMore,
    InfoOutlined,
    LocalShipping,
    TrendingUp,
    TrendingDown,
    PendingActions,
    ArrowUpward,
    ArrowDownward,
    ArrowForward,
    PictureAsPdf,
} from "@mui/icons-material";
import { orderService, OrderPaymentWithOrder } from "../../services/orderService";
import { expenseService } from "../../services/expenseService";
import {
    procurementService,
    PurchasePaymentWithPurchase,
} from "../../services/procurementService";
import { assetService } from "../../services/assetService";
import { Asset } from "../../types/assets";
import { PaymentMethod, Expense } from "../../types";
import { formatCurrencyBDT } from "../../utils/currency";
import { PAYMENT_METHOD_LABELS } from "../../utils/paymentMethods";
import { useCodFeeRate } from "../../hooks/useCodFeeRate";
import {
    CashFlowReportPDFGenerator,
    CashFlowReportData,
} from "../../utils/cashFlowReportPdfGenerator";

// ─── Local helpers ─────────────────────────────────────────────────────────
// Plain local-calendar-day formatting — deliberately NOT toISOString(),
// which shifts across midnight in any timezone ahead of UTC (Bangladesh is
// UTC+6) and would silently show the wrong day for part of the morning.
const toLocalDateStr = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

// Converts a local date+time (as picked in a `datetime-local` field, e.g.
// "2026-08-10T14:30") into the precise UTC instant it represents, so the
// range is filtered correctly no matter what timezone the API server
// itself runs in. Per the ECMAScript spec, a bare "YYYY-MM-DD" parses as
// UTC midnight, while "YYYY-MM-DDTHH:mm:ss" (no offset) parses as *local*
// time, which is what we want here before handing it to toISOString().
// `datetime-local` inputs carry no seconds, so the boundary is pushed to
// the very start/end of the picked minute — picking 18:00 as an end time
// means "through 18:00:59.999", not "up to but excluding 18:00:00".
const localDateTimeStartUtc = (dateTimeStr: string): string =>
    new Date(`${dateTimeStr}:00.000`).toISOString();

const localDateTimeEndUtc = (dateTimeStr: string): string =>
    new Date(`${dateTimeStr}:59.999`).toISOString();

// Asset.purchase_date has no time component (see model comment) — only
// the calendar-date portion of a picked date+time is meaningful for it.
const extractDatePart = (dateTimeStr: string): string =>
    dateTimeStr.slice(0, 10);

// Cash physically collected in hand — either at the POS counter or by our
// own in-house delivery staff — is what the opening/closing balance
// reconciles. COD here means in-house-collected COD, handed over the same
// day. Every other medium (bKash, card, bank transfer, ...) settles
// digitally and doesn't need a physical count. Third-party courier COD
// (SteadFast, ...) is deliberately NOT included here — that money sits
// with the courier and is remitted later, minus their fee — see the
// separate Third-Party Courier Collections section below.
const PHYSICAL_CASH_METHODS: PaymentMethod[] = ["CASH", "COD"];

type MediumTotals = Partial<Record<PaymentMethod, number>>;

const addToMedium = (
    totals: MediumTotals,
    method: PaymentMethod,
    amount: number,
) => {
    totals[method] = (totals[method] || 0) + amount;
};

const sumMedium = (totals: MediumTotals): number =>
    Object.values(totals).reduce((sum, v) => sum + (v || 0), 0);

const cashEquivalent = (totals: MediumTotals): number =>
    PHYSICAL_CASH_METHODS.reduce((sum, m) => sum + (totals[m] || 0), 0);

const DRAWER_ORDER_LIMIT = 500; // generous ceiling for a single day's expenses/assets

// ─── Date+time range persistence ────────────────────────────────────────────
// The chosen range is remembered locally per browser so reopening the tool
// (or reloading mid-shift) comes back to whatever was last applied, instead
// of resetting to today every time.
const DATE_RANGE_STORAGE_KEY = "cps-cash-flow-datetime-range";

interface DateRange {
    // "YYYY-MM-DDTHH:mm" — the native <input type="datetime-local"> value
    // format, stored as-is so it can be fed straight back into the field.
    fromDateTime: string;
    toDateTime: string;
}

const loadStoredDateRange = (): DateRange => {
    try {
        const raw = localStorage.getItem(DATE_RANGE_STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (
                typeof parsed?.fromDateTime === "string" &&
                typeof parsed?.toDateTime === "string"
            ) {
                return {
                    fromDateTime: parsed.fromDateTime,
                    toDateTime: parsed.toDateTime,
                };
            }
        }
    } catch {
        // Corrupt/unavailable storage — fall through to today's default.
    }
    // Defaults to the full current calendar day, matching this tool's
    // original single-day behavior before a range could be picked.
    const today = toLocalDateStr(new Date());
    return { fromDateTime: `${today}T00:00`, toDateTime: `${today}T23:59` };
};

// A same-day cancellation isn't real cash flow — never counted, in any
// section, regardless of when the (now-void) payment was recorded.
const isCancelledPayment = (p: OrderPaymentWithOrder) =>
    p.order_status === "CANCELLED";

// A counter sale: no delivery service at all (in-house and third-party
// orders always have one), and either explicitly tagged OFFLINE or
// untagged entirely — a manually-created order defaults to no channel
// unless staff pick one, so untagged is treated as offline by default
// (see order_channel_type below) rather than being silently invisible.
const isPosPayment = (p: OrderPaymentWithOrder) =>
    !isCancelledPayment(p) &&
    !p.delivery_service_type &&
    (p.order_channel_type === "OFFLINE" || !p.order_channel_id);

const isInHousePayment = (p: OrderPaymentWithOrder) =>
    !isCancelledPayment(p) && p.delivery_service_type === "INHOUSE";

const isThirdPartyPayment = (p: OrderPaymentWithOrder) =>
    !isCancelledPayment(p) && p.delivery_service_type === "THIRD_PARTY";

// Lightweight per-order view built from a flat payment list — one order
// can have multiple payments on the same day (e.g. a split cash + bKash
// checkout), so these are grouped back together for display (itemized
// lists, courier breakdowns) exactly like the previous order-shaped data.
interface OrderPaymentRow {
    order_id: string;
    order_number: string;
    customer_name?: string;
    delivery_service_name?: string;
    order_channel_id?: string;
    payment_transactions: { payment_method: PaymentMethod; amount: number }[];
    // What we pay the delivery partner for this order — a fixed per-order
    // cost, not per-payment. Only meaningful (and only ever netted against
    // anything) for third-party orders with a COD collection — see
    // thirdPartyCodBreakdown below.
    delivery_cost: number;
}

const groupPaymentsByOrder = (
    payments: OrderPaymentWithOrder[],
): OrderPaymentRow[] => {
    const byOrder = new Map<string, OrderPaymentRow>();
    for (const p of payments) {
        let row = byOrder.get(p.order_id);
        if (!row) {
            row = {
                order_id: p.order_id,
                order_number: p.order_number,
                customer_name: p.customer_name,
                delivery_service_name: p.delivery_service_name,
                order_channel_id: p.order_channel_id,
                payment_transactions: [],
                delivery_cost: Number(p.delivery_cost) || 0,
            };
            byOrder.set(p.order_id, row);
        }
        row.payment_transactions.push({
            payment_method: p.payment_method,
            amount: Number(p.amount),
        });
    }
    return Array.from(byOrder.values());
};

// ─── Small presentational pieces ────────────────────────────────────────────

const MediumBreakdownTable: React.FC<{ totals: MediumTotals }> = ({
    totals,
}) => {
    const entries = Object.entries(totals) as [PaymentMethod, number][];
    if (entries.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No payments recorded.
            </Typography>
        );
    }
    return (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Method</TableCell>
                        <TableCell align="right">Amount</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {entries
                        .sort((a, b) => b[1] - a[1])
                        .map(([method, amount]) => (
                            <TableRow key={method}>
                                <TableCell>
                                    <Chip
                                        label={PAYMENT_METHOD_LABELS[method]}
                                        size="small"
                                        color={
                                            PHYSICAL_CASH_METHODS.includes(
                                                method,
                                            )
                                                ? "success"
                                                : "default"
                                        }
                                        variant={
                                            PHYSICAL_CASH_METHODS.includes(
                                                method,
                                            )
                                                ? "filled"
                                                : "outlined"
                                        }
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                    >
                                        {formatCurrencyBDT(amount)}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const SectionTotal: React.FC<{
    label: string;
    amount: number;
    color?: "primary" | "success" | "error";
}> = ({ label, amount, color = "primary" }) => (
    <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={1.5}
    >
        <Typography variant="subtitle1" fontWeight="bold">
            {label}
        </Typography>
        <Typography variant="h6" fontWeight="bold" color={`${color}.main`}>
            {formatCurrencyBDT(amount)}
        </Typography>
    </Box>
);

// Headline figure for the top-of-page KPI row — purely a different view of
// numbers already computed elsewhere on this page (totalIncome,
// totalSpending, netCashChange, thirdPartyCodNetExpected); it introduces no
// new calculation.
const StatTile: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: number;
    tone: "success" | "error" | "warning" | "primary";
    caption?: string;
}> = ({ icon, label, value, tone, caption }) => (
    <Paper
        variant="outlined"
        sx={{
            p: 2,
            height: "100%",
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            borderLeft: 4,
            borderLeftColor: `${tone}.main`,
        }}
    >
        <Box
            sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                bgcolor: (theme) => alpha(theme.palette[tone].main, 0.12),
                color: `${tone}.main`,
            }}
        >
            {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" noWrap>
                {label}
            </Typography>
            <Typography
                variant="h6"
                fontWeight="bold"
                color={`${tone}.main`}
                noWrap
            >
                {formatCurrencyBDT(value)}
            </Typography>
            {caption && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                >
                    {caption}
                </Typography>
            )}
        </Box>
    </Paper>
);

// Consistent icon-badge header, reused by every card on this page — same
// icon/title/tooltip each card already had, just styled once instead of
// five times.
const CardSectionHeader: React.FC<{
    icon: React.ReactNode;
    title: string;
    tone: "success" | "error" | "primary" | "warning";
    tooltip?: string;
}> = ({ icon, title, tone, tooltip }) => (
    <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <Box
            sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                bgcolor: (theme) => alpha(theme.palette[tone].main, 0.12),
                color: `${tone}.main`,
            }}
        >
            {icon}
        </Box>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {title}
        </Typography>
        {tooltip && (
            <Tooltip title={tooltip}>
                <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
        )}
    </Box>
);

// Subsection label + a count chip — same "title — N noun(s)" information
// every subsection already showed, just split into a scannable title and a
// pill instead of one run-on sentence. `title` is omitted where the card
// header already names the section (e.g. Third-Party Courier Collections).
const SubsectionLabel: React.FC<{
    title?: string;
    count: number;
    noun: string;
    pluralNoun?: string;
}> = ({ title, count, noun, pluralNoun }) => (
    <Box
        display="flex"
        alignItems="center"
        justifyContent={title ? "space-between" : "flex-start"}
        mb={1}
    >
        {title && (
            <Typography variant="subtitle2" fontWeight={600}>
                {title}
            </Typography>
        )}
        <Chip
            label={`${count} ${count === 1 ? noun : pluralNoun || `${noun}s`}`}
            size="small"
            variant="outlined"
        />
    </Box>
);

// Same "Subtotal — amount" row repeated after every subsection.
const SubtotalRow: React.FC<{ amount: number }> = ({ amount }) => (
    <Box display="flex" justifyContent="space-between" mt={1}>
        <Typography variant="body2" color="text.secondary">
            Subtotal
        </Typography>
        <Typography variant="body2" fontWeight="bold">
            {formatCurrencyBDT(amount)}
        </Typography>
    </Box>
);

// A small reference line under a row's primary text — order number, or a
// truncated entity ID with the full value in a hover tooltip — so every
// itemized row stays traceable back to its source record without adding a
// dedicated column to already-tight tables.
const RowIdCaption: React.FC<{ label: string; fullId?: string }> = ({
    label,
    fullId,
}) => (
    <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        title={fullId}
    >
        {label}
    </Typography>
);

const shortId = (id: string): string =>
    id.length > 8 ? `${id.slice(0, 8)}…` : id;

// ─── Main page ───────────────────────────────────────────────────────────────

export const DailyCashFlowAnalyzer: React.FC = () => {
    // `appliedRange` is what's actually fetched/displayed and persisted to
    // localStorage. The two date+time fields below edit a separate draft
    // that only takes effect — and only gets saved — when Apply is pressed,
    // so idle typing/picking never triggers a refetch or clobbers the saved
    // range.
    // Standard COD handling fee couriers deduct before remitting collected
    // cash to us — applied AFTER the delivery cost is taken out, not on the
    // raw COD amount (e.g. ৳100 COD − ৳12 delivery cost = ৳88, then 1% of
    // that ৳88). Configurable at Settings → Accounting Setup rather than a
    // hardcoded constant — see hooks/useCodFeeRate.ts.
    const thirdPartyCodFeeRate = useCodFeeRate();

    const [appliedRange, setAppliedRange] = useState<DateRange>(
        loadStoredDateRange,
    );
    const [draftFromDateTime, setDraftFromDateTime] = useState(
        appliedRange.fromDateTime,
    );
    const [draftToDateTime, setDraftToDateTime] = useState(
        appliedRange.toDateTime,
    );
    const [rangeError, setRangeError] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [pdfGenerating, setPdfGenerating] = useState(false);

    // Every order payment received today, regardless of which day the
    // order itself was placed or delivered — e.g. a payment recorded
    // today against a week-old order counts as today's cash flow. Split
    // into POS/in-house/third-party buckets below, purely from each
    // payment's own order context (channel, delivery service).
    const [allOrderPayments, setAllOrderPayments] = useState<
        OrderPaymentWithOrder[]
    >([]);
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const [purchasePayments, setPurchasePayments] = useState<
        PurchasePaymentWithPurchase[]
    >([]);
    const [assetPurchases, setAssetPurchases] = useState<Asset[]>([]);

    const fetchData = useCallback(
        async (fromDateTimeStr: string, toDateTimeStr: string) => {
            setLoading(true);
            setError("");
            try {
                const rangeStartUtc = localDateTimeStartUtc(fromDateTimeStr);
                const rangeEndUtc = localDateTimeEndUtc(toDateTimeStr);
                const [
                    orderPaymentsResult,
                    expensesResult,
                    purchasePaymentsResult,
                    assetsResult,
                ] = await Promise.all([
                    // Every order payment dated within the range, across
                    // every order — catches a payment made in-range on an
                    // order placed or delivered on any other day, not just
                    // brand-new orders.
                    orderService.searchOrderPayments(
                        rangeStartUtc,
                        rangeEndUtc,
                    ),
                    expenseService.searchExpenses(
                        {
                            expense_date_from: rangeStartUtc,
                            expense_date_to: rangeEndUtc,
                        },
                        0,
                        DRAWER_ORDER_LIMIT,
                    ),
                    procurementService.searchPurchasePayments(
                        rangeStartUtc,
                        rangeEndUtc,
                    ),
                    // Asset purchase_date has no time component — only the
                    // calendar-date portion of the picked range applies.
                    assetService.listAssets({
                        purchase_date_from:
                            extractDatePart(fromDateTimeStr),
                        purchase_date_to: extractDatePart(toDateTimeStr),
                        limit: DRAWER_ORDER_LIMIT,
                    }),
                ]);

                setAllOrderPayments(orderPaymentsResult);
                setExpenses(
                    expensesResult.items || expensesResult.data || [],
                );
                setPurchasePayments(purchasePaymentsResult);
                setAssetPurchases(assetsResult);
            } catch (err: any) {
                setError(
                    err?.response?.data?.detail ||
                        err?.message ||
                        "Failed to load data for the selected date range. Please try again.",
                );
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    useEffect(() => {
        fetchData(appliedRange.fromDateTime, appliedRange.toDateTime);
    }, [appliedRange, fetchData]);

    const hasUnappliedChanges =
        draftFromDateTime !== appliedRange.fromDateTime ||
        draftToDateTime !== appliedRange.toDateTime;

    const handleApplyRange = useCallback(() => {
        if (draftFromDateTime > draftToDateTime) {
            setRangeError("Start date/time must be on or before end date/time.");
            return;
        }
        setRangeError("");
        const next: DateRange = {
            fromDateTime: draftFromDateTime,
            toDateTime: draftToDateTime,
        };
        setAppliedRange(next);
        try {
            localStorage.setItem(
                DATE_RANGE_STORAGE_KEY,
                JSON.stringify(next),
            );
        } catch {
            // localStorage may be unavailable (private browsing, quota) —
            // the range still applies for this session, it just won't
            // persist for next time.
        }
    }, [draftFromDateTime, draftToDateTime]);

    // ── Aggregation ──────────────────────────────────────────────────────
    // Bucket today's payments purely by their order's own context — one
    // order can have multiple payments (see groupPaymentsByOrder), so
    // these stay as flat transaction lists until grouped for display.
    const posPayments = useMemo(
        () => allOrderPayments.filter(isPosPayment),
        [allOrderPayments],
    );
    const inHousePayments = useMemo(
        () => allOrderPayments.filter(isInHousePayment),
        [allOrderPayments],
    );
    const thirdPartyPayments = useMemo(
        () => allOrderPayments.filter(isThirdPartyPayment),
        [allOrderPayments],
    );

    const posOrders = useMemo(
        () => groupPaymentsByOrder(posPayments),
        [posPayments],
    );
    const inHouseOrders = useMemo(
        () => groupPaymentsByOrder(inHousePayments),
        [inHousePayments],
    );
    const thirdPartyOrders = useMemo(
        () => groupPaymentsByOrder(thirdPartyPayments),
        [thirdPartyPayments],
    );

    // Surfaced to staff so untagged sales get noticed and fixed at the
    // source, rather than the fallback silently masking the gap forever.
    const untaggedPosCount = useMemo(
        () => posOrders.filter((o) => !o.order_channel_id).length,
        [posOrders],
    );

    const posByMedium = useMemo(() => {
        const totals: MediumTotals = {};
        for (const order of posOrders) {
            for (const t of order.payment_transactions || []) {
                addToMedium(totals, t.payment_method, Number(t.amount));
            }
        }
        return totals;
    }, [posOrders]);
    const posTotal = useMemo(() => sumMedium(posByMedium), [posByMedium]);

    const inHouseByMedium = useMemo(() => {
        const totals: MediumTotals = {};
        for (const order of inHouseOrders) {
            for (const t of order.payment_transactions || []) {
                addToMedium(totals, t.payment_method, Number(t.amount));
            }
        }
        return totals;
    }, [inHouseOrders]);
    const inHouseTotal = useMemo(
        () => sumMedium(inHouseByMedium),
        [inHouseByMedium],
    );

    const inHouseByCourier = useMemo(() => {
        const totals: Record<string, number> = {};
        for (const order of inHouseOrders) {
            const courier = order.delivery_service_name || "Unknown";
            const orderTotal = (order.payment_transactions || []).reduce(
                (sum, t) => sum + Number(t.amount),
                0,
            );
            totals[courier] = (totals[courier] || 0) + orderTotal;
        }
        return totals;
    }, [inHouseOrders]);

    // Same courier totals as above, split by payment method — lets staff
    // see e.g. how much of Kawsar's collection was Cash vs. bKash, not just
    // the combined figure.
    const inHouseByCourierAndMedium = useMemo(() => {
        const totals: Record<string, MediumTotals> = {};
        for (const order of inHouseOrders) {
            const courier = order.delivery_service_name || "Unknown";
            if (!totals[courier]) totals[courier] = {};
            for (const t of order.payment_transactions || []) {
                addToMedium(totals[courier], t.payment_method, Number(t.amount));
            }
        }
        return totals;
    }, [inHouseOrders]);

    // ── Third-party courier collections (COD) ───────────────────────────
    // A third-party order can be split-paid — e.g. a customer pays part
    // upfront via bKash and the rest COD on delivery. Only the COD portion
    // is ever actually held by the courier (subject to their remittance
    // fee); every other medium is money that came directly to us, exactly
    // like a POS or in-house payment, so it belongs in real income — not
    // in the "pending" bucket below.
    const thirdPartyByMedium = useMemo(() => {
        const totals: MediumTotals = {};
        for (const order of thirdPartyOrders) {
            for (const t of order.payment_transactions || []) {
                addToMedium(totals, t.payment_method, Number(t.amount));
            }
        }
        return totals;
    }, [thirdPartyOrders]);

    const thirdPartyByCourier = useMemo(() => {
        const totals: Record<string, number> = {};
        for (const order of thirdPartyOrders) {
            const courier = order.delivery_service_name || "Unknown";
            const orderTotal = (order.payment_transactions || []).reduce(
                (sum, t) => sum + Number(t.amount),
                0,
            );
            totals[courier] = (totals[courier] || 0) + orderTotal;
        }
        return totals;
    }, [thirdPartyOrders]);

    // Non-COD medium(s) on third-party orders — already real money in our
    // own accounts, folded into income below (see incomeByMedium).
    const thirdPartyDirectByMedium = useMemo(() => {
        const { COD, ...rest } = thirdPartyByMedium;
        return rest as MediumTotals;
    }, [thirdPartyByMedium]);
    const thirdPartyDirectTotal = useMemo(
        () => sumMedium(thirdPartyDirectByMedium),
        [thirdPartyDirectByMedium],
    );

    // Only the COD portion is actually held by the courier and subject to
    // their remittance deductions. delivery_cost is a fixed per-ORDER cost
    // (not per-payment), and it comes out of the courier's COD collection
    // before their percentage fee is applied — e.g. ৳100 COD − ৳12
    // delivery cost = ৳88, then 1% of ৳88 = ৳0.88, so ৳87.12 comes back.
    // Computed per order (not from the flat thirdPartyByMedium total)
    // because delivery_cost must only be subtracted once per order, and
    // only for orders that actually have a COD collection — a third-party
    // order paid entirely upfront via bKash/card never has COD money
    // passing through the courier's hands, so nothing to net here.
    const thirdPartyCodBreakdown = useMemo(() => {
        let gross = 0;
        let deliveryCost = 0;
        let fee = 0;
        let netExpected = 0;
        for (const order of thirdPartyOrders) {
            const codAmount = (order.payment_transactions || [])
                .filter((t) => t.payment_method === "COD")
                .reduce((sum, t) => sum + Number(t.amount), 0);
            if (codAmount <= 0) continue;
            const orderDeliveryCost = order.delivery_cost || 0;
            const afterDeliveryCost = codAmount - orderDeliveryCost;
            const orderFee = afterDeliveryCost * thirdPartyCodFeeRate;
            gross += codAmount;
            deliveryCost += orderDeliveryCost;
            fee += orderFee;
            netExpected += afterDeliveryCost - orderFee;
        }
        return { gross, deliveryCost, fee, netExpected };
    }, [thirdPartyOrders, thirdPartyCodFeeRate]);

    const thirdPartyCodGross = thirdPartyCodBreakdown.gross;
    const thirdPartyCodDeliveryCost = thirdPartyCodBreakdown.deliveryCost;
    const thirdPartyCodFee = thirdPartyCodBreakdown.fee;
    const thirdPartyCodNetExpected = thirdPartyCodBreakdown.netExpected;

    const expensesByMedium = useMemo(() => {
        const totals: MediumTotals = {};
        for (const e of expenses) {
            addToMedium(totals, e.payment_method, Number(e.amount));
        }
        return totals;
    }, [expenses]);
    const expensesTotal = useMemo(
        () => sumMedium(expensesByMedium),
        [expensesByMedium],
    );

    const purchasePaymentsByMedium = useMemo(() => {
        const totals: MediumTotals = {};
        for (const p of purchasePayments) {
            addToMedium(totals, p.payment_method as PaymentMethod, Number(p.amount));
        }
        return totals;
    }, [purchasePayments]);
    const purchasePaymentsTotal = useMemo(
        () => sumMedium(purchasePaymentsByMedium),
        [purchasePaymentsByMedium],
    );

    const assetPurchasesByMedium = useMemo(() => {
        const totals: MediumTotals = {};
        for (const a of assetPurchases) {
            // payment_method is only absent on assets recorded before this
            // field existed — nothing to attribute those to, so they're
            // skipped here rather than guessed at.
            if (a.payment_method) {
                addToMedium(totals, a.payment_method, Number(a.purchase_cost));
            }
        }
        return totals;
    }, [assetPurchases]);
    const assetPurchasesTotal = useMemo(
        () => sumMedium(assetPurchasesByMedium),
        [assetPurchasesByMedium],
    );

    const incomeByMedium = useMemo(() => {
        const totals: MediumTotals = {};
        for (const [m, v] of Object.entries(posByMedium))
            addToMedium(totals, m as PaymentMethod, v || 0);
        for (const [m, v] of Object.entries(inHouseByMedium))
            addToMedium(totals, m as PaymentMethod, v || 0);
        // Direct (non-COD) payments on third-party orders — real money
        // already in our own accounts, see thirdPartyDirectByMedium above.
        for (const [m, v] of Object.entries(thirdPartyDirectByMedium))
            addToMedium(totals, m as PaymentMethod, v || 0);
        return totals;
    }, [posByMedium, inHouseByMedium, thirdPartyDirectByMedium]);
    const totalIncome = posTotal + inHouseTotal + thirdPartyDirectTotal;

    const spendingByMedium = useMemo(() => {
        const totals: MediumTotals = {};
        for (const [m, v] of Object.entries(expensesByMedium))
            addToMedium(totals, m as PaymentMethod, v || 0);
        for (const [m, v] of Object.entries(purchasePaymentsByMedium))
            addToMedium(totals, m as PaymentMethod, v || 0);
        for (const [m, v] of Object.entries(assetPurchasesByMedium))
            addToMedium(totals, m as PaymentMethod, v || 0);
        return totals;
    }, [expensesByMedium, purchasePaymentsByMedium, assetPurchasesByMedium]);
    const totalSpending = expensesTotal + purchasePaymentsTotal + assetPurchasesTotal;

    const cashIn = cashEquivalent(incomeByMedium);
    const cashOut = cashEquivalent(spendingByMedium);
    const netCashChange = cashIn - cashOut;

    // Final recap — every medium that moved any money today, received vs
    // spent, side by side. incomeByMedium/spendingByMedium already exclude
    // third-party COD (it's not received yet), so this stays accurate.
    const allMediumsSummary = useMemo(() => {
        const methods = new Set<PaymentMethod>([
            ...(Object.keys(incomeByMedium) as PaymentMethod[]),
            ...(Object.keys(spendingByMedium) as PaymentMethod[]),
        ]);
        return Array.from(methods)
            .map((method) => {
                const received = incomeByMedium[method] || 0;
                const spent = spendingByMedium[method] || 0;
                return { method, received, spent, net: received - spent };
            })
            .sort((a, b) => b.received - a.received);
    }, [incomeByMedium, spendingByMedium]);

    // Builds the PDF's data purely from figures already computed above for
    // the on-screen report — never recomputed — so the PDF can't diverge
    // numerically from what's on the page.
    const handleDownloadPdf = useCallback(async () => {
        setPdfGenerating(true);
        try {
            const fromLabel = new Date(
                `${appliedRange.fromDateTime}:00`,
            ).toLocaleString();
            const toLabel = new Date(
                `${appliedRange.toDateTime}:00`,
            ).toLocaleString();

            const reportData: CashFlowReportData = {
                dateRangeLabel: `${fromLabel} — ${toLabel}`,
                kpis: {
                    totalIncome,
                    totalSpending,
                    netCashChange,
                    pendingCodRemittance: thirdPartyCodNetExpected,
                },
                income: {
                    pos: {
                        count: posOrders.length,
                        byMedium: posByMedium,
                        subtotal: posTotal,
                        untaggedCount: untaggedPosCount,
                    },
                    inHouse: {
                        count: inHouseOrders.length,
                        byMedium: inHouseByMedium,
                        subtotal: inHouseTotal,
                        byCourier: inHouseByCourier,
                    },
                    totalIncome,
                    thirdPartyDirectTotal,
                },
                spending: {
                    expenses: {
                        count: expenses.length,
                        byMedium: expensesByMedium,
                        subtotal: expensesTotal,
                    },
                    procurement: {
                        count: purchasePayments.length,
                        byMedium: purchasePaymentsByMedium,
                        subtotal: purchasePaymentsTotal,
                    },
                    assets: {
                        count: assetPurchases.length,
                        byMedium: assetPurchasesByMedium,
                        subtotal: assetPurchasesTotal,
                    },
                    totalSpending,
                },
                cashReconciliation: {
                    cashIn,
                    cashOut,
                    netCashChange,
                    totalIncomeAllMediums: totalIncome,
                    totalSpendingAllMediums: totalSpending,
                    netAllMediums: totalIncome - totalSpending,
                },
                thirdParty: {
                    orderCount: thirdPartyOrders.length,
                    byCourier: thirdPartyByCourier,
                    directByMedium: thirdPartyDirectByMedium,
                    directTotal: thirdPartyDirectTotal,
                    codGross: thirdPartyCodGross,
                    codDeliveryCost: thirdPartyCodDeliveryCost,
                    codFeeRatePct: thirdPartyCodFeeRate * 100,
                    codFee: thirdPartyCodFee,
                    codNetExpected: thirdPartyCodNetExpected,
                },
                allMediumsSummary,
            };

            const generator = new CashFlowReportPDFGenerator();
            const fromFile = appliedRange.fromDateTime.slice(0, 10);
            const toFile = appliedRange.toDateTime.slice(0, 10);
            await generator.download(
                reportData,
                `Cash-Flow-Report_${fromFile}_to_${toFile}.pdf`,
            );
        } catch (err: any) {
            setError(
                err?.message || "Failed to generate PDF. Please try again.",
            );
        } finally {
            setPdfGenerating(false);
        }
    }, [
        appliedRange,
        totalIncome,
        totalSpending,
        netCashChange,
        thirdPartyCodNetExpected,
        posOrders,
        posByMedium,
        posTotal,
        untaggedPosCount,
        inHouseOrders,
        inHouseByMedium,
        inHouseTotal,
        inHouseByCourier,
        thirdPartyDirectTotal,
        expenses,
        expensesByMedium,
        expensesTotal,
        purchasePayments,
        purchasePaymentsByMedium,
        purchasePaymentsTotal,
        assetPurchases,
        assetPurchasesByMedium,
        assetPurchasesTotal,
        cashIn,
        cashOut,
        thirdPartyOrders,
        thirdPartyByCourier,
        thirdPartyDirectByMedium,
        thirdPartyCodGross,
        thirdPartyCodDeliveryCost,
        thirdPartyCodFee,
        thirdPartyCodFeeRate,
        allMediumsSummary,
    ]);

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
                mb={3}
            >
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.12),
                            color: "primary.main",
                        }}
                    >
                        <AccountBalanceWallet />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight="bold">
                            Cash Flow Analyzer
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Offline sales, in-house delivery collections,
                            expenses, supplier payments and asset purchases
                            for the selected date range — nothing here is
                            saved to the database.
                        </Typography>
                    </Box>
                </Box>
                <Paper
                    variant="outlined"
                    sx={{
                        p: 1.5,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 0.5,
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <TextField
                            label="From"
                            type="datetime-local"
                            size="small"
                            value={draftFromDateTime}
                            onChange={(e) =>
                                setDraftFromDateTime(e.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 210 }}
                        />
                        <TextField
                            label="To"
                            type="datetime-local"
                            size="small"
                            value={draftToDateTime}
                            onChange={(e) =>
                                setDraftToDateTime(e.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 210 }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleApplyRange}
                            disabled={!hasUnappliedChanges}
                        >
                            Apply
                        </Button>
                    </Box>
                    {rangeError && (
                        <Typography variant="caption" color="error">
                            {rangeError}
                        </Typography>
                    )}
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                            pdfGenerating ? (
                                <CircularProgress size={16} />
                            ) : (
                                <PictureAsPdf />
                            )
                        }
                        onClick={handleDownloadPdf}
                        disabled={loading || pdfGenerating}
                        sx={{ alignSelf: "flex-end" }}
                    >
                        {pdfGenerating ? "Generating…" : "Download PDF"}
                    </Button>
                </Paper>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {!loading && !error && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatTile
                            icon={<TrendingUp />}
                            label="Total Income"
                            value={totalIncome}
                            tone="success"
                            caption="All mediums"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatTile
                            icon={<TrendingDown />}
                            label="Total Spending"
                            value={totalSpending}
                            tone="error"
                            caption="All mediums"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatTile
                            icon={<AccountBalanceWallet />}
                            label="Net Cash Change"
                            value={netCashChange}
                            tone={netCashChange >= 0 ? "success" : "error"}
                            caption="Cash & COD in hand"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatTile
                            icon={<PendingActions />}
                            label="Pending COD Remittance"
                            value={thirdPartyCodNetExpected}
                            tone="warning"
                            caption="From third-party couriers"
                        />
                    </Grid>
                </Grid>
            )}

            {loading ? (
                <Box display="flex" justifyContent="center" py={8}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {/* Income */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card>
                            <CardContent>
                                <CardSectionHeader
                                    icon={<Storefront />}
                                    title="Income"
                                    tone="success"
                                />

                                {/* POS */}
                                <SubsectionLabel
                                    title="Offline (POS) Sales"
                                    count={posOrders.length}
                                    noun="order"
                                />
                                {untaggedPosCount > 0 && (
                                    <Alert severity="warning" sx={{ mb: 1 }}>
                                        {untaggedPosCount} of these orders{" "}
                                        {untaggedPosCount !== 1 ? "were" : "was"}{" "}
                                        created without an Order Channel set,
                                        so counted here as Offline by
                                        default. Pick a channel on the order
                                        form next time to keep this accurate.
                                    </Alert>
                                )}
                                <MediumBreakdownTable totals={posByMedium} />
                                {posOrders.length > 0 && (
                                    <Accordion
                                        variant="outlined"
                                        sx={{ mt: 1, boxShadow: "none" }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMore />}
                                        >
                                            <Typography variant="caption">
                                                Show sales list
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            <OrderListTable
                                                orders={posOrders}
                                                showCustomer
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                                <SubtotalRow amount={posTotal} />

                                <Divider sx={{ my: 2 }} />

                                {/* In-house delivery */}
                                <SubsectionLabel
                                    title="In-house Delivery Collections"
                                    count={inHouseOrders.length}
                                    noun="order"
                                />

                                {Object.keys(inHouseByCourier).length > 0 && (
                                    <TableContainer
                                        component={Paper}
                                        variant="outlined"
                                        sx={{ mb: 1.5 }}
                                    >
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        Courier
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Collected
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.entries(
                                                    inHouseByCourier,
                                                )
                                                    .sort(
                                                        (a, b) => b[1] - a[1],
                                                    )
                                                    .map(
                                                        ([
                                                            courier,
                                                            amount,
                                                        ]) => (
                                                            <TableRow
                                                                key={courier}
                                                            >
                                                                <TableCell>
                                                                    <Typography variant="body2">
                                                                        {courier}
                                                                    </Typography>
                                                                    <Box
                                                                        sx={{
                                                                            display: "flex",
                                                                            flexWrap: "wrap",
                                                                            gap: 0.5,
                                                                            mt: 0.5,
                                                                        }}
                                                                    >
                                                                        {Object.entries(
                                                                            inHouseByCourierAndMedium[
                                                                                courier
                                                                            ] || {},
                                                                        )
                                                                            .sort(
                                                                                (a, b) =>
                                                                                    b[1] - a[1],
                                                                            )
                                                                            .map(
                                                                                ([
                                                                                    method,
                                                                                    methodAmount,
                                                                                ]) => (
                                                                                    <Chip
                                                                                        key={
                                                                                            method
                                                                                        }
                                                                                        label={`${PAYMENT_METHOD_LABELS[method as PaymentMethod]} ${formatCurrencyBDT(methodAmount)}`}
                                                                                        size="small"
                                                                                        variant={
                                                                                            PHYSICAL_CASH_METHODS.includes(
                                                                                                method as PaymentMethod,
                                                                                            )
                                                                                                ? "filled"
                                                                                                : "outlined"
                                                                                        }
                                                                                        color={
                                                                                            PHYSICAL_CASH_METHODS.includes(
                                                                                                method as PaymentMethod,
                                                                                            )
                                                                                                ? "success"
                                                                                                : "default"
                                                                                        }
                                                                                        sx={{
                                                                                            height: 20,
                                                                                            fontSize:
                                                                                                "0.7rem",
                                                                                            "& .MuiChip-label":
                                                                                                {
                                                                                                    px: 0.75,
                                                                                                },
                                                                                        }}
                                                                                    />
                                                                                ),
                                                                            )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {formatCurrencyBDT(
                                                                        amount,
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        ),
                                                    )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}

                                <MediumBreakdownTable
                                    totals={inHouseByMedium}
                                />
                                {inHouseOrders.length > 0 && (
                                    <Accordion
                                        variant="outlined"
                                        sx={{ mt: 1, boxShadow: "none" }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMore />}
                                        >
                                            <Typography variant="caption">
                                                Show delivery list
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            <OrderListTable
                                                orders={inHouseOrders}
                                                showCourier
                                                showCustomer
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                                <SubtotalRow amount={inHouseTotal} />

                                <Divider sx={{ my: 1.5 }} />
                                <SectionTotal
                                    label="Total Income"
                                    amount={totalIncome}
                                    color="success"
                                />
                                {thirdPartyDirectTotal > 0 && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{ textAlign: "right", mt: 0.5 }}
                                    >
                                        Including{" "}
                                        {formatCurrencyBDT(
                                            thirdPartyDirectTotal,
                                        )}{" "}
                                        paid directly to us via courier
                                        deliveries (see Third-Party Courier
                                        Collections below)
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Spending */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card>
                            <CardContent>
                                <CardSectionHeader
                                    icon={<ShoppingCart />}
                                    title="Spending"
                                    tone="error"
                                />

                                {/* Expenses */}
                                <SubsectionLabel
                                    title="Expenses"
                                    count={expenses.length}
                                    noun="entry"
                                    pluralNoun="entries"
                                />
                                <MediumBreakdownTable
                                    totals={expensesByMedium}
                                />
                                {expenses.length > 0 && (
                                    <Accordion
                                        variant="outlined"
                                        sx={{ mt: 1, boxShadow: "none" }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMore />}
                                        >
                                            <Typography variant="caption">
                                                Show expense list
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>
                                                                Vendor / Category
                                                            </TableCell>
                                                            <TableCell>
                                                                Method
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                Amount
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {expenses.map(
                                                            (e) => (
                                                                <TableRow
                                                                    key={
                                                                        e.expense_id
                                                                    }
                                                                >
                                                                    <TableCell>
                                                                        {e.vendor_name ||
                                                                            e.category
                                                                                ?.name ||
                                                                            "-"}
                                                                        <RowIdCaption
                                                                            label={`ID: ${shortId(
                                                                                e.expense_id,
                                                                            )}`}
                                                                            fullId={
                                                                                e.expense_id
                                                                            }
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {
                                                                            PAYMENT_METHOD_LABELS[
                                                                                e.payment_method
                                                                            ]
                                                                        }
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {formatCurrencyBDT(
                                                                            Number(
                                                                                e.amount,
                                                                            ),
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ),
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                                <SubtotalRow amount={expensesTotal} />

                                <Divider sx={{ my: 2 }} />

                                {/* Procurement payments */}
                                <SubsectionLabel
                                    title="Procurement Payments"
                                    count={purchasePayments.length}
                                    noun="payment"
                                />
                                <MediumBreakdownTable
                                    totals={purchasePaymentsByMedium}
                                />
                                {purchasePayments.length > 0 && (
                                    <Accordion
                                        variant="outlined"
                                        sx={{ mt: 1, boxShadow: "none" }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMore />}
                                        >
                                            <Typography variant="caption">
                                                Show payment list
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>
                                                                PO #
                                                            </TableCell>
                                                            <TableCell>
                                                                Supplier
                                                            </TableCell>
                                                            <TableCell>
                                                                Method
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                Amount
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {purchasePayments.map(
                                                            (p) => (
                                                                <TableRow
                                                                    key={
                                                                        p.payment_id
                                                                    }
                                                                >
                                                                    <TableCell>
                                                                        {
                                                                            p.purchase_number
                                                                        }
                                                                        <RowIdCaption
                                                                            label={`Payment ID: ${shortId(
                                                                                p.payment_id,
                                                                            )}`}
                                                                            fullId={
                                                                                p.payment_id
                                                                            }
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {p.supplier_name ||
                                                                            "-"}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {
                                                                            PAYMENT_METHOD_LABELS[
                                                                                p.payment_method as PaymentMethod
                                                                            ]
                                                                        }
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {formatCurrencyBDT(
                                                                            Number(
                                                                                p.amount,
                                                                            ),
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ),
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                                <SubtotalRow amount={purchasePaymentsTotal} />

                                <Divider sx={{ my: 2 }} />

                                {/* Asset purchases */}
                                <SubsectionLabel
                                    title="Asset Purchases"
                                    count={assetPurchases.length}
                                    noun="item"
                                />
                                <MediumBreakdownTable
                                    totals={assetPurchasesByMedium}
                                />
                                {assetPurchases.length > 0 && (
                                    <Accordion
                                        variant="outlined"
                                        sx={{ mt: 1, boxShadow: "none" }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMore />}
                                        >
                                            <Typography variant="caption">
                                                Show asset list
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>
                                                                Asset
                                                            </TableCell>
                                                            <TableCell>
                                                                Method
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                Amount
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {assetPurchases.map(
                                                            (a) => (
                                                                <TableRow
                                                                    key={
                                                                        a.asset_id
                                                                    }
                                                                >
                                                                    <TableCell>
                                                                        {
                                                                            a.name
                                                                        }
                                                                        <RowIdCaption
                                                                            label={`ID: ${shortId(
                                                                                a.asset_id,
                                                                            )}`}
                                                                            fullId={
                                                                                a.asset_id
                                                                            }
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {a.payment_method
                                                                            ? PAYMENT_METHOD_LABELS[
                                                                                  a
                                                                                      .payment_method
                                                                              ]
                                                                            : "-"}
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {formatCurrencyBDT(
                                                                            Number(
                                                                                a.purchase_cost,
                                                                            ),
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ),
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                                <SubtotalRow amount={assetPurchasesTotal} />

                                <Divider sx={{ my: 1.5 }} />
                                <SectionTotal
                                    label="Total Spending"
                                    amount={totalSpending}
                                    color="error"
                                />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Cash Reconciliation */}
                    <Grid size={{ xs: 12 }}>
                        <Card>
                            <CardContent>
                                <CardSectionHeader
                                    icon={<Receipt />}
                                    title="Cash Reconciliation"
                                    tone="primary"
                                    tooltip="Only Cash and in-house-collected COD (money physically collected in hand — at the counter or by your own delivery staff) feed into this. bKash, card, bank transfer and other digital mediums settle on their own and don't need a physical count. COD collected by third-party couriers is tracked separately below — that money sits with the courier until they remit it."
                                />

                                <Grid container spacing={2} alignItems="stretch">
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={0.5}
                                        >
                                            <ArrowUpward
                                                fontSize="small"
                                                sx={{ color: "success.main" }}
                                            />
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Cash &amp; COD In
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            color="success.main"
                                        >
                                            {formatCurrencyBDT(cashIn)}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={0.5}
                                        >
                                            <ArrowDownward
                                                fontSize="small"
                                                sx={{ color: "error.main" }}
                                            />
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Cash &amp; COD Out
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            color="error.main"
                                        >
                                            {formatCurrencyBDT(cashOut)}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                                textAlign: {
                                                    xs: "left",
                                                    sm: "right",
                                                },
                                                bgcolor: (theme) =>
                                                    alpha(
                                                        theme.palette[
                                                            netCashChange >= 0
                                                                ? "success"
                                                                : "error"
                                                        ].main,
                                                        0.08,
                                                    ),
                                                borderColor:
                                                    netCashChange >= 0
                                                        ? "success.main"
                                                        : "error.main",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                = Net Cash Change
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                fontWeight="bold"
                                                color={
                                                    netCashChange >= 0
                                                        ? "success.main"
                                                        : "error.main"
                                                }
                                            >
                                                {formatCurrencyBDT(
                                                    netCashChange,
                                                )}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2 }} />

                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Total Income (all mediums)
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {formatCurrencyBDT(totalIncome)}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Total Spending (all mediums)
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {formatCurrencyBDT(totalSpending)}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Net (all mediums)
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                            color={
                                                totalIncome - totalSpending >=
                                                0
                                                    ? "success.main"
                                                    : "error.main"
                                            }
                                        >
                                            {formatCurrencyBDT(
                                                totalIncome - totalSpending,
                                            )}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Third-party courier collections (COD) */}
                    <Grid size={{ xs: 12 }}>
                        <Card>
                            <CardContent>
                                <CardSectionHeader
                                    icon={<LocalShipping />}
                                    title="Third-Party Courier Collections"
                                    tone="warning"
                                    tooltip="Orders delivered in the selected date range by a third-party courier (e.g. SteadFast). Only the COD portion is held by the courier and excluded from Total Income — it's remitted later, minus the order's delivery cost and their handling fee (applied to what's left after the delivery cost). Any other medium (bKash, card, ...) on these orders came directly to us, so it's already counted in Total Income above, same as a POS or in-house sale."
                                />

                                <SubsectionLabel
                                    count={thirdPartyOrders.length}
                                    noun="order"
                                />

                                {Object.keys(thirdPartyByCourier).length >
                                    0 && (
                                    <TableContainer
                                        component={Paper}
                                        variant="outlined"
                                        sx={{ mb: 1.5 }}
                                    >
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        Courier
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Collected
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.entries(
                                                    thirdPartyByCourier,
                                                )
                                                    .sort(
                                                        (a, b) => b[1] - a[1],
                                                    )
                                                    .map(
                                                        ([
                                                            courier,
                                                            amount,
                                                        ]) => (
                                                            <TableRow
                                                                key={courier}
                                                            >
                                                                <TableCell>
                                                                    {courier}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {formatCurrencyBDT(
                                                                        amount,
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        ),
                                                    )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}

                                {thirdPartyDirectTotal > 0 && (
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            gutterBottom
                                        >
                                            Paid directly to us (already
                                            included in Total Income above)
                                        </Typography>
                                        <MediumBreakdownTable
                                            totals={thirdPartyDirectByMedium}
                                        />
                                    </Box>
                                )}
                                {thirdPartyOrders.length > 0 && (
                                    <Accordion
                                        variant="outlined"
                                        sx={{ mt: 1, boxShadow: "none" }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMore />}
                                        >
                                            <Typography variant="caption">
                                                Show order list
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            <OrderListTable
                                                orders={thirdPartyOrders}
                                                showCourier
                                                showCustomer
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                <Divider sx={{ my: 2 }} />

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    gutterBottom
                                >
                                    Collected via COD (pending remittance —
                                    not counted in Total Income)
                                </Typography>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        bgcolor: (theme) =>
                                            alpha(
                                                theme.palette.warning.main,
                                                0.06,
                                            ),
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        flexWrap="wrap"
                                        alignItems="center"
                                        gap={2}
                                    >
                                        <Box sx={{ flex: "1 1 140px" }}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Gross COD Collected
                                            </Typography>
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                            >
                                                {formatCurrencyBDT(
                                                    thirdPartyCodGross,
                                                )}
                                            </Typography>
                                        </Box>
                                        <ArrowForward
                                            fontSize="small"
                                            sx={{
                                                color: "text.disabled",
                                                display: {
                                                    xs: "none",
                                                    sm: "block",
                                                },
                                            }}
                                        />
                                        <Box sx={{ flex: "1 1 140px" }}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                − Delivery Cost
                                            </Typography>
                                            <Typography
                                                variant="h6"
                                                color="error.main"
                                            >
                                                {formatCurrencyBDT(
                                                    thirdPartyCodDeliveryCost,
                                                )}
                                            </Typography>
                                        </Box>
                                        <ArrowForward
                                            fontSize="small"
                                            sx={{
                                                color: "text.disabled",
                                                display: {
                                                    xs: "none",
                                                    sm: "block",
                                                },
                                            }}
                                        />
                                        <Box sx={{ flex: "1 1 180px" }}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                − Est. Courier Fee (
                                                {thirdPartyCodFeeRate * 100}
                                                % of remainder)
                                            </Typography>
                                            <Typography
                                                variant="h6"
                                                color="error.main"
                                            >
                                                {formatCurrencyBDT(
                                                    thirdPartyCodFee,
                                                )}
                                            </Typography>
                                        </Box>
                                        <ArrowForward
                                            fontSize="small"
                                            sx={{
                                                color: "text.disabled",
                                                display: {
                                                    xs: "none",
                                                    sm: "block",
                                                },
                                            }}
                                        />
                                        <Box sx={{ flex: "1 1 160px" }}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                = Expected Net Remittance
                                            </Typography>
                                            <Typography
                                                variant="h5"
                                                fontWeight="bold"
                                                color="primary.main"
                                            >
                                                {formatCurrencyBDT(
                                                    thirdPartyCodNetExpected,
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>

                                {thirdPartyCodGross > 0 && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        This isn't cash in hand yet —
                                        confirm the expected net remittance
                                        above shows up in the courier's
                                        account once they settle it.
                                    </Alert>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Final recap — every medium, received vs spent */}
                    <Grid size={{ xs: 12 }}>
                        <Card>
                            <CardContent>
                                <CardSectionHeader
                                    icon={<AccountBalanceWallet />}
                                    title="All Payment Mediums — Summary"
                                    tone="primary"
                                    tooltip="Every medium that moved money in the selected date range, received vs spent. Third-party COD is deliberately excluded — that money hasn't been received yet, see the section above."
                                />

                                {allMediumsSummary.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        No activity recorded for this date range.
                                    </Typography>
                                ) : (
                                    <TableContainer
                                        component={Paper}
                                        variant="outlined"
                                    >
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow
                                                    sx={{
                                                        "& th": {
                                                            fontWeight: 700,
                                                            bgcolor: (
                                                                theme,
                                                            ) =>
                                                                alpha(
                                                                    theme
                                                                        .palette
                                                                        .text
                                                                        .primary,
                                                                    0.04,
                                                                ),
                                                        },
                                                    }}
                                                >
                                                    <TableCell>
                                                        Medium
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Received
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Spent
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Net
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {allMediumsSummary.map(
                                                    ({
                                                        method,
                                                        received,
                                                        spent,
                                                        net,
                                                    }) => (
                                                        <TableRow
                                                            key={method}
                                                            hover
                                                        >
                                                            <TableCell>
                                                                <Chip
                                                                    label={
                                                                        PAYMENT_METHOD_LABELS[
                                                                            method
                                                                        ]
                                                                    }
                                                                    size="small"
                                                                    color={
                                                                        PHYSICAL_CASH_METHODS.includes(
                                                                            method,
                                                                        )
                                                                            ? "success"
                                                                            : "default"
                                                                    }
                                                                    variant={
                                                                        PHYSICAL_CASH_METHODS.includes(
                                                                            method,
                                                                        )
                                                                            ? "filled"
                                                                            : "outlined"
                                                                    }
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {formatCurrencyBDT(
                                                                    received,
                                                                )}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {formatCurrencyBDT(
                                                                    spent,
                                                                )}
                                                            </TableCell>
                                                            <TableCell
                                                                align="right"
                                                                sx={{
                                                                    fontWeight:
                                                                        "bold",
                                                                    color:
                                                                        net >= 0
                                                                            ? "success.main"
                                                                            : "error.main",
                                                                }}
                                                            >
                                                                {formatCurrencyBDT(
                                                                    net,
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ),
                                                )}
                                                <TableRow
                                                    sx={{
                                                        "& td": {
                                                            borderTop: 2,
                                                            borderTopColor:
                                                                "divider",
                                                            bgcolor: (
                                                                theme,
                                                            ) =>
                                                                alpha(
                                                                    theme
                                                                        .palette
                                                                        .text
                                                                        .primary,
                                                                    0.04,
                                                                ),
                                                        },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="bold"
                                                        >
                                                            Total
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        {formatCurrencyBDT(
                                                            totalIncome,
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        {formatCurrencyBDT(
                                                            totalSpending,
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            fontWeight: "bold",
                                                            color:
                                                                totalIncome -
                                                                    totalSpending >=
                                                                0
                                                                    ? "success.main"
                                                                    : "error.main",
                                                        }}
                                                    >
                                                        {formatCurrencyBDT(
                                                            totalIncome -
                                                                totalSpending,
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
        </Container>
    );
};

// ─── Itemized list (shared by POS and in-house sections) ───────────────────

const OrderListTable: React.FC<{
    orders: OrderPaymentRow[];
    showCourier?: boolean;
    showCustomer?: boolean;
}> = ({ orders, showCourier = false, showCustomer = false }) => (
    <TableContainer>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>{showCustomer ? "Customer" : "Order #"}</TableCell>
                    {showCourier && <TableCell>Courier</TableCell>}
                    <TableCell>Method(s)</TableCell>
                    <TableCell align="right">Amount</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {orders.map((order) => {
                    const transactions = order.payment_transactions || [];
                    const total = transactions.reduce(
                        (sum, t) => sum + Number(t.amount),
                        0,
                    );
                    return (
                        <TableRow key={order.order_id}>
                            <TableCell>
                                {showCustomer
                                    ? order.customer_name || "Walk-in Customer"
                                    : order.order_number}
                                {showCustomer && (
                                    <RowIdCaption
                                        label={`Order #${order.order_number}`}
                                    />
                                )}
                            </TableCell>
                            {showCourier && (
                                <TableCell>
                                    {order.delivery_service_name || "-"}
                                </TableCell>
                            )}
                            <TableCell>
                                {transactions.length === 0
                                    ? "-"
                                    : transactions
                                          .map(
                                              (t) =>
                                                  PAYMENT_METHOD_LABELS[
                                                      t.payment_method
                                                  ],
                                          )
                                          .join(", ")}
                            </TableCell>
                            <TableCell align="right">
                                {formatCurrencyBDT(total)}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    </TableContainer>
);

export default DailyCashFlowAnalyzer;
